<script setup lang="ts">
import { computed } from 'vue'
import { marked } from 'marked'
import hljs from 'highlight.js/lib/core'
import bash from 'highlight.js/lib/languages/bash'
import javascript from 'highlight.js/lib/languages/javascript'
import python from 'highlight.js/lib/languages/python'
import go from 'highlight.js/lib/languages/go'
import json from 'highlight.js/lib/languages/json'
import 'highlight.js/styles/atom-one-dark.css'
import { useI18n } from '../composables/useI18n'

// 仅注册文档中使用的语言
hljs.registerLanguage('bash', bash)
hljs.registerLanguage('javascript', javascript)
hljs.registerLanguage('python', python)
hljs.registerLanguage('go', go)
hljs.registerLanguage('json', json)

const props = defineProps<{
  markdown: string
  vars?: Record<string, string>
}>()

const { t } = useI18n()

// 模板变量替换：{{KEY}} → 传入的 vars 值，未提供则原样保留
const applyVars = (source: string): string => {
  if (!props.vars) return source
  return source.replace(/\{\{(\w+)\}\}/g, (match, key: string) => props.vars?.[key] ?? match)
}

// marked 配置：代码块交给 highlight.js 高亮
marked.setOptions({
  gfm: true,
  breaks: false,
})

const langLabel: Record<string, string> = {
  bash: 'curl',
  javascript: 'JavaScript',
  python: 'Python',
  go: 'Go',
  json: 'JSON',
}

const renderer = new marked.Renderer()
renderer.code = ({ text, lang }) => {
  const language = lang || 'plaintext'
  let highlighted: string
  try {
    highlighted = hljs.getLanguage(language) ? hljs.highlight(text, { language }).value : escapeHtml(text)
  } catch {
    highlighted = escapeHtml(text)
  }
  const label = langLabel[language] ?? language
  // 代码块容器：标题栏（语言标识 + 复制按钮）+ 高亮代码
  return `<div class="code-block">
  <div class="code-block-header">
    <span class="code-block-lang">${escapeHtml(label)}</span>
    <button type="button" class="code-block-copy" data-code="${escapeHtml(encodeURIComponent(text))}">${escapeHtml(t('markdownViewer.copy'))}</button>
  </div>
  <pre class="hljs"><code>${highlighted}</code></pre>
</div>`
}

// h2 渲染为可锚点定位的标题（id 规则与 apiIntegrationDocs 的 anchor 解析保持一致）
renderer.heading = ({ text, depth }) => {
  if (depth !== 2) {
    const tag = `h${depth}`
    return `<${tag}>${text}</${tag}>`
  }
  const anchor = text
    .toLowerCase()
    .replace(/[`*]/g, '')
    .replace(/[^\w\u4e00-\u9fa5]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `<h2 id="${anchor}">${text}</h2>`
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const rendered = computed(() => marked.parse(applyVars(props.markdown), { renderer }) as string)

// 事件委托：点击复制按钮时拷贝对应代码
const onBodyClick = async (e: MouseEvent) => {
  const btn = (e.target as HTMLElement).closest('.code-block-copy') as HTMLButtonElement | null
  if (!btn) return
  const raw = decodeURIComponent(btn.dataset.code || '')
  try {
    await navigator.clipboard.writeText(raw)
    btn.textContent = t('markdownViewer.copied')
    setTimeout(() => {
      btn.textContent = t('markdownViewer.copy')
    }, 1500)
  } catch {
    btn.textContent = t('markdownViewer.copyFailed')
    setTimeout(() => {
      btn.textContent = t('markdownViewer.copy')
    }, 1500)
  }
}
</script>

<template>
  <div class="markdown-body" @click="onBodyClick" v-html="rendered"></div>
</template>

<style scoped>
@reference "../style.css";

.markdown-body {
  @apply text-sm text-gray-700 dark:text-gray-300 leading-relaxed;
}

.markdown-body :deep(h1) {
  @apply text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700;
}

.markdown-body :deep(h2) {
  @apply text-base font-semibold text-gray-800 dark:text-gray-100 mt-6 mb-3;
}

.markdown-body :deep(h3) {
  @apply text-sm font-semibold text-gray-800 dark:text-gray-100 mt-4 mb-2;
}

.markdown-body :deep(p) {
  @apply my-2;
}

.markdown-body :deep(ul), .markdown-body :deep(ol) {
  @apply my-2 pl-5 list-disc space-y-1;
}

.markdown-body :deep(li) {
  @apply text-sm;
}

.markdown-body :deep(code) {
  @apply bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 text-xs font-mono text-pink-600 dark:text-pink-400;
}

/* 代码块容器 */
.markdown-body :deep(.code-block) {
  @apply my-3 rounded-lg overflow-hidden border border-gray-700/60;
}

.markdown-body :deep(.code-block-header) {
  @apply flex items-center justify-between bg-gray-800/90 dark:bg-gray-900 px-3 py-1.5 border-b border-gray-700/60;
}

.markdown-body :deep(.code-block-lang) {
  @apply text-[10px] font-semibold uppercase tracking-wider text-gray-400;
}

.markdown-body :deep(.code-block-copy) {
  @apply text-[11px] text-gray-300 bg-gray-700/60 hover:bg-gray-600 rounded px-2 py-0.5 transition-colors cursor-pointer;
}

.markdown-body :deep(pre) {
  @apply my-0 rounded-none overflow-x-auto;
}

.markdown-body :deep(pre code) {
  @apply block p-4 text-xs font-mono leading-relaxed bg-gray-900 dark:bg-gray-950 text-gray-100 whitespace-pre;
  background: transparent;
}

.markdown-body :deep(table) {
  @apply w-full my-3 text-xs border-collapse;
}

.markdown-body :deep(th) {
  @apply bg-gray-50 dark:bg-gray-800/80 border border-gray-100 dark:border-gray-700 px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300;
}

.markdown-body :deep(td) {
  @apply border border-gray-100 dark:border-gray-700 px-3 py-2 text-gray-600 dark:text-gray-300 align-top;
}

.markdown-body :deep(blockquote) {
  @apply my-3 border-l-4 border-blue-200 dark:border-blue-800 pl-4 py-1 text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-950/20 rounded-r-lg;
}

.markdown-body :deep(strong) {
  @apply text-gray-800 dark:text-gray-100;
}
</style>
